<script setup>
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useSupportTicketFormatters } from '../composables/useSupportTicketFormatters';

defineProps({
  selectedWorkOrder: { type: Object, required: true },
  assistancePermissions: { type: Object, default: () => ({}) },
  assistanceResponsibilityOptions: { type: Array, default: () => [] },
  evidenceKindOptions: { type: Array, default: () => [] },
  canReopenAssistance: { type: Boolean, default: false },
  canApproveAssistanceBudget: { type: Boolean, default: false },
  canRecordAssistanceCost: { type: Boolean, default: false },
  canRecordAssistanceEvidence: { type: Boolean, default: false },
  isReopeningAssistance: { type: Boolean, default: false },
  isApprovingAssistanceBudget: { type: Boolean, default: false },
  isRecordingAssistanceCost: { type: Boolean, default: false },
  isRecordingAssistanceEvidence: { type: Boolean, default: false },
});

const emit = defineEmits([
  'reopen',
  'approveBudget',
  'recordCost',
  'recordEvidence',
]);

const assistanceReopenForm = defineModel('reopenForm', {
  type: Object,
  required: true,
});
const assistanceCostForm = defineModel('costForm', {
  type: Object,
  required: true,
});
const assistanceEvidenceForm = defineModel('evidenceForm', {
  type: Object,
  required: true,
});

const { t } = useI18n();
const { formatAssistanceMoney } = useSupportTicketFormatters();
</script>

<template>
  <div class="grid gap-4">
    <div
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3 sm:grid-cols-2"
    >
      <div>
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.BUDGET') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{
            selectedWorkOrder.financials_restricted &&
            selectedWorkOrder.has_budget
              ? t('SUPPORT_TICKETS.ASSISTANCE.RESTRICTED')
              : formatAssistanceMoney(
                  selectedWorkOrder.budget_amount,
                  selectedWorkOrder.currency
                )
          }}
        </p>
      </div>
      <div>
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.COST') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{
            selectedWorkOrder.financials_restricted &&
            selectedWorkOrder.has_cost
              ? t('SUPPORT_TICKETS.ASSISTANCE.RESTRICTED')
              : formatAssistanceMoney(
                  selectedWorkOrder.cost_amount,
                  selectedWorkOrder.currency
                )
          }}
        </p>
      </div>
    </div>

    <form
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
      @submit.prevent="emit('reopen')"
    >
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.REOPEN.REASON')">
        <textarea
          v-model.trim="assistanceReopenForm.reason"
          rows="2"
          class="min-h-[72px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
      <div class="grid gap-3 sm:grid-cols-2">
        <LaisField
          :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_LABEL')"
        >
          <input
            v-model.trim="assistanceReopenForm.evidence_label"
            type="text"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          />
        </LaisField>
        <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_URL')">
          <input
            v-model.trim="assistanceReopenForm.evidence_url"
            type="url"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          />
        </LaisField>
      </div>
      <div class="flex justify-end">
        <LaisPrimaryButton
          type="submit"
          icon="i-lucide-rotate-ccw"
          variant="ghost"
          :is-loading="isReopeningAssistance"
          :disabled="!canReopenAssistance"
        >
          {{ t('SUPPORT_TICKETS.ASSISTANCE.REOPEN.SUBMIT') }}
        </LaisPrimaryButton>
      </div>
    </form>

    <div class="grid gap-4 lg:grid-cols-2">
      <div
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
      >
        <div>
          <p class="text-[12px] font-semibold text-lais-text">
            {{ t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.TITLE') }}
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{
              selectedWorkOrder.budget_approved
                ? t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.APPROVED')
                : t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.PENDING')
            }}
          </p>
        </div>
        <LaisPrimaryButton
          icon="i-lucide-badge-check"
          :is-loading="isApprovingAssistanceBudget"
          :disabled="!canApproveAssistanceBudget"
          @click="emit('approveBudget')"
        >
          {{ t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.SUBMIT') }}
        </LaisPrimaryButton>
        <p
          v-if="!assistancePermissions.can_approve_budget"
          class="text-[11px] text-lais-text-muted"
        >
          {{ t('SUPPORT_TICKETS.ASSISTANCE.APPROVAL.PERMISSION') }}
        </p>
      </div>

      <form
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
        @submit.prevent="emit('recordCost')"
      >
        <p class="text-[12px] font-semibold text-lais-text">
          {{ t('SUPPORT_TICKETS.ASSISTANCE.COST.TITLE') }}
        </p>
        <div class="grid gap-3 sm:grid-cols-2">
          <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.COST')">
            <input
              v-model.trim="assistanceCostForm.amount"
              type="number"
              min="0"
              step="0.01"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
            />
          </LaisField>
          <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.BEARER')">
            <select
              v-model="assistanceCostForm.bearer"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
            >
              <option
                v-for="item in assistanceResponsibilityOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </option>
            </select>
          </LaisField>
        </div>
        <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.NOTES')">
          <input
            v-model.trim="assistanceCostForm.notes"
            type="text"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          />
        </LaisField>
        <div class="flex justify-end">
          <LaisPrimaryButton
            type="submit"
            icon="i-lucide-receipt-text"
            :is-loading="isRecordingAssistanceCost"
            :disabled="!canRecordAssistanceCost"
          >
            {{ t('SUPPORT_TICKETS.ASSISTANCE.COST.SUBMIT') }}
          </LaisPrimaryButton>
        </div>
      </form>
    </div>

    <form
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
      @submit.prevent="emit('recordEvidence')"
    >
      <p class="text-[12px] font-semibold text-lais-text">
        {{ t('SUPPORT_TICKETS.ASSISTANCE.EVIDENCE.TITLE') }}
      </p>
      <div class="grid gap-3 sm:grid-cols-3">
        <LaisField
          :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_KIND')"
        >
          <select
            v-model="assistanceEvidenceForm.evidence_kind"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          >
            <option
              v-for="item in evidenceKindOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </LaisField>
        <LaisField
          :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_LABEL')"
        >
          <input
            v-model.trim="assistanceEvidenceForm.evidence_label"
            type="text"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          />
        </LaisField>
        <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_URL')">
          <input
            v-model.trim="assistanceEvidenceForm.evidence_url"
            type="url"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          />
        </LaisField>
      </div>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.EVIDENCE.BODY')">
        <textarea
          v-model.trim="assistanceEvidenceForm.body"
          rows="2"
          class="min-h-[72px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
      <div class="flex justify-end">
        <LaisPrimaryButton
          type="submit"
          icon="i-lucide-paperclip"
          variant="ghost"
          :is-loading="isRecordingAssistanceEvidence"
          :disabled="!canRecordAssistanceEvidence"
        >
          {{ t('SUPPORT_TICKETS.ASSISTANCE.EVIDENCE.SUBMIT') }}
        </LaisPrimaryButton>
      </div>
    </form>
  </div>
</template>
